<script lang="js" setup>
import { ref, onMounted, computed, watch } from "vue";

const allData = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const itemsPerPage = ref(10);
const API_BASE_URL = process.env.VUE_APP_BASE_URL;

// Filters
const filters = ref({
  status: '',
  categoryName: '',
  logNo: ''
});

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('token');

// Common headers for API calls
const getHeaders = () => ({
  'Authorization': `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json'
});

// Filtered data computed property
const filteredData = computed(() => {
  let filtered = allData.value;
  
  // Apply category filter
  if (filters.value.categoryName.trim()) {
    const categorySearch = filters.value.categoryName.toLowerCase().trim();
    filtered = filtered.filter(item => 
      item.categoryName.toLowerCase().includes(categorySearch)
    );
  }
  
  // Apply logNo filter
  if (filters.value.logNo.trim()) {
    const logSearch = filters.value.logNo.toLowerCase().trim();
    filtered = filtered.filter(item => 
      item.logNo.toLowerCase().includes(logSearch)
    );
  }
  
  return filtered;
});

// Update total items when filters change
const filteredTotalItems = computed(() => filteredData.value.length);

// Computed property for paginated data
const paginatedData = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  const endIndex = startIndex + itemsPerPage.value;
  return filteredData.value.slice(startIndex, endIndex);
});

// Computed property to format the data
const formattedData = computed(() => {
  return paginatedData.value.map(item => ({
    id: item._id,
    siteName: item.kytOperation?.site?.siteName || 'N/A',
    categoryName: item.categoryName,
    logNo: item.logNo,
    requestDate: new Date(item.requestCloseDate).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    status: item.status,
    problem: getKytDataDetails(item),
    solution: getKytDataSolution(item)
  }));
});

// Helper function to get KYT data details
const getKytDataDetails = (item) => {
  const category = item.categoryName;
  const kytData = item.kytOperation?.kytData?.[category] || [];
  const matchingLog = kytData.find(data => data.logNo === item.logNo);
  return matchingLog?.problem || 'N/A';
};

// Helper function to get KYT data solution
const getKytDataSolution = (item) => {
  const category = item.categoryName;
  const kytData = item.kytOperation?.kytData?.[category] || [];
  const matchingLog = kytData.find(data => data.logNo === item.logNo);
  return matchingLog?.solution || 'N/A';
};

// Update computed property for total pages based on filtered data
const computedTotalPages = computed(() => {
  return Math.ceil(filteredTotalItems.value / itemsPerPage.value);
});

// Computed property for pagination array
const pages = computed(() => {
  const pagesArray = [];
  const maxVisiblePages = 5;
  const totalPagesValue = computedTotalPages.value;
  
  let startPage = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPagesValue, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pagesArray.push(i);
  }
  return pagesArray;
});

// Build query string for backend filters (status only)
const buildQueryString = () => {
  const queryParams = new URLSearchParams();
  queryParams.append('page', '1'); // Always fetch first page as we're handling pagination locally
  queryParams.append('limit', '1000'); // Fetch more items as we're handling pagination locally
  
  if (filters.value.status) {
    queryParams.append('status', filters.value.status);
  }
  
  return queryParams.toString();
};

const fetchData = async () => {
  try {
    const queryString = buildQueryString();
    const response = await fetch(`${API_BASE_URL}api/kyt/close/requests?${queryString}`, {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data?.success && data?.data?.kytCloseRequests) {
      allData.value = data.data.kytCloseRequests;
      // Reset to page 1 when new data is fetched
      currentPage.value = 1;
    }
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

// Pagination methods
const goToPage = (page) => {
  if (page >= 1 && page <= computedTotalPages.value && page !== currentPage.value) {
    currentPage.value = page;
  }
};

const nextPage = () => {
  if (currentPage.value < computedTotalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Watch for changes in status filter only
watch(() => filters.value.status, () => {
  fetchData();
});

// Reset page when local filters change
watch([() => filters.value.categoryName, () => filters.value.logNo], () => {
  currentPage.value = 1;
}, { deep: true });

onMounted(() => {
  fetchData();
});
</script>

<template>
  <Breadcrumbs main="Apps" title="KYT Close Requests" />
  <div class="container-fluid">
    <div class="card">
      <div class="card-body">
        <!-- Filters Section -->
        <div class="row mb-3">
          <div class="col-md-3">
            <select v-model="filters.status" class="form-select">
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div class="col-md-3">
            <input 
              type="text" 
              v-model="filters.categoryName" 
              class="form-control" 
              placeholder="Filter by Category"
            >
          </div>
          <div class="col-md-3">
            <input 
              type="text" 
              v-model="filters.logNo" 
              class="form-control" 
              placeholder="Filter by Log No"
            >
          </div>
        </div>

        <div class="table-responsive">
          <div class="mb-3">
            <h5>Total Requests: {{ filteredTotalItems }}</h5>
          </div>
          <table class="table table-bordered table-striped table-hover">
            <thead class="bg-light">
              <tr>
                <th>Site Name</th>
                <th>Category</th>
                <th>Log No</th>
                <th>Request Date</th>
                <th>Problem</th>
                <th>Solution</th>
              </tr>
            </thead>
            <tbody v-if="!formattedData.length">
              <tr>
                <td colspan="7" class="text-center py-4">No requests found</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="item in formattedData" :key="item.id">
                <td class="text-nowrap">{{ item.siteName }}</td>
                <td class="text-capitalize">{{ item.categoryName }}</td>
                <td class="text-nowrap">
                  <router-link :to="`/kyt-close/log/${item.logNo}`" class="text-primary text-decoration-none">
                    {{ item.logNo }}
                  </router-link>
                </td>
                <td class="text-nowrap">{{ item.requestDate }}</td>
                <td>
                  <div class="text-wrap" style="max-width: 200px;">{{ item.problem }}</div>
                </td>
                <td>
                  <div class="text-wrap" style="max-width: 200px;">{{ item.solution }}</div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Updated Pagination Section -->
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
            <div class="mb-2 mb-md-0">
              Showing page {{ currentPage }} of {{ computedTotalPages }} (Total items: {{ filteredTotalItems }})
            </div>
            <nav aria-label="Page navigation" v-if="computedTotalPages > 1">
              <ul class="pagination justify-content-end mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" @click="previousPage" style="cursor: pointer;">
                    <i class="fa fa-angle-left"></i>
                  </a>
                </li>
                
                <li v-if="pages[0] > 1" class="page-item">
                  <a class="page-link" @click="goToPage(1)">1</a>
                </li>
                <li v-if="pages[0] > 2" class="page-item disabled">
                  <span class="page-link">...</span>
                </li>
                
                <li 
                  v-for="page in pages" 
                  :key="page" 
                  class="page-item"
                  :class="{ active: page === currentPage }"
                >
                  <a class="page-link" @click="goToPage(page)">{{ page }}</a>
                </li>
                
                <li v-if="pages[pages.length - 1] < computedTotalPages - 1" class="page-item disabled">
                  <span class="page-link">...</span>
                </li>
                <li v-if="pages[pages.length - 1] < computedTotalPages" class="page-item">
                  <a class="page-link" @click="goToPage(computedTotalPages)">{{ computedTotalPages }}</a>
                </li>
                
                <li class="page-item" :class="{ disabled: currentPage === computedTotalPages }">
                  <a class="page-link" @click="nextPage" style="cursor: pointer;">
                    <i class="fa fa-angle-right"></i>
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.badge {
  padding: 0.5em 1em;
  font-size: 0.875em;
  text-transform: capitalize;
}

.btn-group .btn {
  padding: 0.25rem 0.5rem;
}

.btn i {
  font-size: 0.875rem;
}

.table {
  font-size: 0.9rem;
}

.table td, .table th {
  vertical-align: middle;
  padding: 0.5rem;
}

@media (max-width: 768px) {
  .table {
    font-size: 0.8rem;
  }
  
  .btn-group .btn {
    padding: 0.2rem 0.4rem;
  }
  
  .text-wrap {
    max-width: 150px !important;
  }
}

@media (max-width: 576px) {
  .text-wrap {
    max-width: 100px !important;
  }
  
  .table td, .table th {
    padding: 0.25rem;
  }
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  padding: 0.375rem 0.75rem;
  cursor: pointer;
}

.page-item.active .page-link {
  background-color: #435ebe;
  border-color: #435ebe;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.form-select,
.form-control {
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .pagination .page-link {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
  
  .form-select,
  .form-control {
    font-size: 0.875rem;
  }
}
</style>
